import { useMemo } from 'react'
import { LucideIconName, WebsiteConfigBM } from '@bm-js/atlascloud-shared'
import { getGoogleMapsLink } from '../utils/misc'
import { CLink, LinkComponentType } from './CLink'
import { WavySvg } from './WavySvg'
import { MultilineText } from './MultilineText'
import { CustomerButton } from './CustomerButton'
import { ResponsiveImage } from './ResponsiveImage'
import { Icon } from './Icon'

type Props = {
  LinkComponent: LinkComponentType
  footerText: WebsiteConfigBM['footerText']
  footerImage: WebsiteConfigBM['footerImage']
  socialMedia: WebsiteConfigBM['socialMedia']
  contactInfo: WebsiteConfigBM['contactInfo']
}

type SocialMediaKey = keyof WebsiteConfigBM['socialMedia']

export const Footer2 = ({
  LinkComponent,
  footerText,
  footerImage,
  socialMedia,
  contactInfo,
}: Props) => {
  const addressString = useMemo(() => {
    return `${contactInfo.recipient}, ${contactInfo.adress}, ${contactInfo.postalCode} ${contactInfo.postalArea}`
  }, [contactInfo])

  const googleMapsLink = useMemo(() => {
    return getGoogleMapsLink(contactInfo)
  }, [contactInfo])

  const socialMediaLinks = useMemo(() => {
    return (Object.keys(socialMedia) as SocialMediaKey[])
      .map((key) => {
        const icons: Record<SocialMediaKey, LucideIconName> = {
          instagram: LucideIconName.Instagram,
          facebook: LucideIconName.Facebook,
          twitter: LucideIconName.Twitter,
        }

        const item = socialMedia[key]
        return {
          href: item,
          icon: icons[key],
        }
      })
      .filter((item) => item.href)
  }, [socialMedia])

  return (
    <div className='relative mt-60 @container'>
      <WavySvg className='rotate-test translate-y-130 absolute bottom-full left-0 right-0 scale-y-[200%] fill-customerPrimary-400 ' />
      <footer className='bg-customerPrimary-400 px-8 pt-20 pb-14 text-customerInverse @3xl:px-20'>
        <div className='absolute -top-[15%] right-[20%] overflow-hidden rounded-full @7xl:-top-[40%]'>
          {!!footerImage && (
            <ResponsiveImage
              className='aspect-square w-40 object-cover @7xl:w-96'
              file={footerImage}
            />
          )}
        </div>
        <div className='max-w-2xl'>
          <p className='mb-10 text-xl font-medium @3xl:text-2xl'>
            <MultilineText text={footerText} />
          </p>

          {googleMapsLink && (
            <CLink
              LinkComponent={LinkComponent}
              href={googleMapsLink}
              target='_blank'
              rel='noreferrer'
            >
              <address className='font-light not-italic'>
                {addressString}
              </address>
            </CLink>
          )}
          <CLink
            href={`mailto:${contactInfo.email}`}
            LinkComponent={LinkComponent}
          >
            <CustomerButton
              className='mt-6 mb-36 @3xl:mb-28'
              btnType='outline'
              btnColor='light'
            >
              {contactInfo.email}
            </CustomerButton>
          </CLink>
          <div className='flex flex-col gap-6 @3xl:flex-row @3xl:items-center @3xl:gap-14'>
            <div className='flex items-center gap-6 @3xl:gap-8'>
              {socialMediaLinks.map((item, index) => (
                <CLink
                  key={index}
                  href={item.href}
                  LinkComponent={LinkComponent}
                  target='_blank'
                  rel='noreferrer'
                  className='text-neutral-100'
                >
                  <Icon name={item.icon} className='size-8' />
                </CLink>
              ))}
            </div>
            <p className='text-xs font-light'>
              © Copyright {new Date().getFullYear()}. Alla rättigheter
              förbehållna
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
